import React from 'react';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../../../utils/dateUtils';
import { useAskAicaSurface, ASK_AICA_SURFACE } from '../../context/AskAicaSurfaceContext';
import { leftPanelCloseIcon, rightPanelCloseIcon, AicaLogo } from '../../../../../../../../assets/hostedassets';
import newChatIcon from '../../../../../../../../assets/border-color.svg';
import { useAppSelector } from '../../../../../../../../app/hooks';
import DeleteIcon from '../../../../../../../../assets/Svg/DeleteIcon';
import { Mixpanel } from '../../../../../../../../utils/mixpanel';
import {
    AICA_ASK_AICA_NEW_CHAT_CLICKED,
    AICA_ASK_AICA_DELETE_CHAT_CLICKED,
} from '../../../../../../../../utils/constants/mixpanelEvents/investorEvents';
import styles from './PortfolioConversationSidebar.module.scss';

/** Left sidebar for the org-less portfolio surface: user-scoped conversation
 * history (GET /chat/conversations/user — never the org-scoped list).
 * Conversations v2 (2026-08-12): full multi-conversation history — New Chat,
 * list, resume, and per-row delete — same affordances as the leads
 * ConversationSidebar, minus a "Team's Conversations" section: the portfolio
 * surface has no team-visibility concept, every row already belongs to the
 * caller (the list endpoint is user-scoped, not org-scoped). New Chat resets
 * the view and arms the next send to force-create a conversation (D1/D2) —
 * it never inserts a row itself, so there is no ghost row while the panel is
 * empty. Delete fires on click with no confirmation step (D7 gate delta);
 * outcome is reported via the existing toasts. emptyHint mirrors the leads
 * ConversationSidebar's "No conversations yet" verbatim. 401 (no
 * platformUserId principal) is handled upstream by useGlobalAskAica, which
 * resolves to an empty `conversations` list here — this component just renders
 * that as the same empty state as "no history yet". Reads/writes go through
 * the shared surface context, not props. */
interface PortfolioConversationSidebarProps {
    /** Owned by AskAicaV2 so the LeftPanel header can host the collapsed-state
        affordances (New Chat + expand) instead of a separate 3rem strip. */
    collapsed: boolean;
    onCollapsedChange: (collapsed: boolean) => void;
}

const PortfolioConversationSidebar: React.FC<PortfolioConversationSidebarProps> = ({ collapsed, onCollapsedChange }) => {
    const { conversations, isLoadingConversations, selectedConversationId, loadConversation, newChat, deleteConversation, isClearingChat } =
        useAskAicaSurface();
    // Same source as the global Topbar: the per-domain logo served by the backend,
    // falling back to the bundled AICA mark.
    const logoUrl = useAppSelector((state) => state.user.domainMetadata?.logoUrl);

    const handleNewChat = () => {
        newChat?.();
        Mixpanel.track(AICA_ASK_AICA_NEW_CHAT_CLICKED, { surface: ASK_AICA_SURFACE.PORTFOLIO });
    };

    const handleDeleteClick = (e: React.MouseEvent, conversationId: string) => {
        e.stopPropagation();
        Mixpanel.track(AICA_ASK_AICA_DELETE_CHAT_CLICKED, {
            surface: ASK_AICA_SURFACE.PORTFOLIO,
            scope: conversationId === selectedConversationId ? 'active' : 'background',
        });
        deleteConversation?.(conversationId);
    };

    if (collapsed) {
        return (
            <div className={styles.SidebarCollapsed}>
                <div className={styles.CollapsedTopRow}>
                    <button className={styles.CollapseBtn} onClick={() => onCollapsedChange(false)} title="Expand conversations">
                        <img src={rightPanelCloseIcon} alt="" width="18" height="18" />
                    </button>
                </div>
                <div className={styles.CollapsedBody}>
                    <button className={styles.NewChatBtnCollapsed} onClick={handleNewChat} title="New Chat" aria-label="New Chat">
                        <img className={styles.NewChatIcon} src={newChatIcon} alt="" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.Sidebar} data-testid="portfolio-conversation-sidebar">
            <div className={styles.TopRow}>
                <img className={styles.SidebarLogo} src={logoUrl || AicaLogo} alt="AICA" />
                <button className={styles.CollapseBtn} onClick={() => onCollapsedChange(true)} title="Collapse conversations">
                    <img src={leftPanelCloseIcon} alt="" width="18" height="18" />
                </button>
            </div>
            <button className={styles.NewChatBtn} onClick={handleNewChat}>
                <img className={styles.NewChatIcon} src={newChatIcon} alt="" />
                New Chat
            </button>
            {isLoadingConversations ? (
                <div className={styles.Shimmer}>
                    {[1, 2, 3].map((index) => (
                        <div key={index} className={styles.ShimmerRow} />
                    ))}
                </div>
            ) : (
                <div className={styles.Section}>
                    <span className={styles.SectionHeader}>Your Conversations</span>
                    {conversations.length === 0 ? (
                        <span className={styles.EmptyHint}>No conversations yet</span>
                    ) : (
                        conversations.map((conversation) => (
                            // Outer <div> (not <button>) — a delete <button> cannot nest
                            // inside a resume <button> (invalid HTML, breaks a11y).
                            <div
                                key={conversation.conversation_id}
                                className={`${styles.Row} ${
                                    selectedConversationId === conversation.conversation_id ? styles.RowSelected : ''
                                }`}
                            >
                                <button
                                    type="button"
                                    className={styles.RowMain}
                                    onClick={() => loadConversation?.(conversation.conversation_id)}
                                    disabled={isClearingChat}
                                >
                                    <span className={styles.RowTitle}>{conversation.title || 'Untitled'}</span>
                                    {conversation.last_message_at && (
                                        <span className={styles.RowSubtitle}>
                                            {formatDateByFormat(conversation.last_message_at, DATE_FORMATS.LIBRARY_FILE_TIMESTAMP)}
                                        </span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className={styles.RowDelete}
                                    onClick={(e) => handleDeleteClick(e, conversation.conversation_id)}
                                    aria-label="Delete conversation"
                                    title="Delete conversation"
                                    disabled={isClearingChat}
                                >
                                    <DeleteIcon className="" width="14" height="14" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default PortfolioConversationSidebar;
